import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ArrowLeft, Copy, ExternalLink, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { AdminGate } from "@/components/admin/AdminGate";
import { Button } from "@/components/ui/button";
import { CAMPANHA_LABEL, CONFERIDO_EDUZZ_EM, OFERTAS_LINKS, SEM_VENDEDORA, VENDEDORAS_LINKS, linkVendedora, type Campanha } from "@/lib/black-november/links-vendedoras";

const T = "Admin · Cupons/Links · FEO 2026";
const D = "Todos os checkouts das pós por vendedora e das páginas: Black em Dobro, BLACKFEO, lotes e alunos.";
export const Route = createFileRoute("/admin/black-november-links")({
  head: () => ({ meta: [{ title: T }, { name: "description", content: D }, { property: "og:title", content: T }, { property: "og:description", content: D }, { property: "og:type", content: "website" }, { name: "twitter:card", content: "summary" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: () => <AdminGate route="/admin/black-november-links"><Links /></AdminGate>,
});

const brl = (n: number) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const copiar = async (t: string) => { await navigator.clipboard.writeText(t); toast.success("Link copiado"); };

function Links() {
  const [vend, setVend] = useState<string>("todas");
  const [camp, setCamp] = useState<Campanha | "todas">("todas");
  const TODAS = [...VENDEDORAS_LINKS, SEM_VENDEDORA];
  const vendedoras = vend === "todas" ? TODAS : TODAS.filter((v) => v.nome === vend);
  const ofertas = useMemo(() => OFERTAS_LINKS.filter((o) => camp === "todas" || o.campanha === camp), [camp]);

  return (
    <main className="min-h-screen px-4 py-8 sm:py-12"><div className="mx-auto max-w-6xl space-y-6">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Painel de controle</Link>
      <header className="border-b border-border pb-6"><p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-admin-accent">Testes</p><h1 className="text-2xl font-bold sm:text-3xl">Cupons/Links</h1><p className="mt-2 max-w-3xl text-sm text-muted-foreground">{`Todos os checkouts das pós · 6 vendedoras + links gerais das páginas · preços com cupom conferidos na Eduzz em ${CONFERIDO_EDUZZ_EM}`}</p></header>
      <section className="grid gap-3 sm:grid-cols-3">
        {(["dobro", "blackfeo", "alunofeo"] as const).map((c) => (
          <div key={c} className="rounded-lg border border-border bg-card p-4">
            <p className="text-sm font-semibold">{CAMPANHA_LABEL[c]}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {c === "dobro" && "Vendida pelo CRM durante todo novembro. Venda direta só na oferta exclusiva do fim do mês."}
              {c === "blackfeo" && "Cupom BLACKFEO · 64% OFF (Orto, Dor, Esp, Ger) e 56% OFF (Trauma, Presc)."}
              {c === "alunofeo" && "Cupons ALUNOFEO / ALUNOFEOASS para alunos FEO."}
            </p>
          </div>
        ))}
        <div className="rounded-lg border border-border bg-card p-4 sm:col-span-3">
          <p className="text-sm font-semibold">Lote Promo · Lote 1 · Lote 2</p>
          <p className="mt-1 text-sm text-muted-foreground">Fora da Black: LOTEPROMO/LOTEPROMOASS, LOTE1/LOTE1ASS e Lote 2 sem cupom (preço cheio). Ficam só aqui no painel, não vão para o FEO CRM.
            </p>
          </div>
      </section>

      <section className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm">
        <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> Atenção: em 3 casos o cupom de aluno sai mais caro que o BLACKFEO</p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
          <li>Assinatura Orto/Dor/Esp/Ger: ALUNOFEOASS 15x R$ 198,50 × BLACKFEO 15x R$ 179.</li>
          <li>Única Trauma/Presc: ALUNOFEO R$ 1.297 × BLACKFEO R$ 1.188.</li>
          <li>Assinatura Trauma/Presc: praticamente igual (12x R$ 148,50 × 12x R$ 149).</li>
        </ul>
      </section>

      <div className="flex flex-wrap gap-2">
        {["todas", ...TODAS.map((v) => v.nome)].map((n) => (
          <Button key={n} size="sm" variant={vend === n ? "default" : "outline"} onClick={() => setVend(n)}>{n === "todas" ? "Todas as vendedoras" : n}</Button>
        ))}
        <span className="mx-2 border-l border-border" />
        {(["todas", "dobro", "blackfeo", "alunofeo", "lotepromo", "lote1", "lote2", "lp"] as const).map((c) => (
          <Button key={c} size="sm" variant={camp === c ? "default" : "outline"} onClick={() => setCamp(c)}>{c === "todas" ? "Todas as ofertas" : CAMPANHA_LABEL[c]}</Button>
        ))}
      </div>

      {vendedoras.map((v) => {
        const lista = ofertas.filter((o) => (v.sufixo ? !o.soLp : true));
        if (!lista.length) return null;
        return (
        <section key={v.nome} className="rounded-lg border border-border bg-card">
          <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-4">
            <div>
              <h2 className="text-lg font-bold">{v.nome}</h2>
              <p className="text-xs text-muted-foreground">{!v.sufixo ? "Links usados nas páginas e envios gerais, sem afiliado nem marcação de vendedora" : v.afiliado ? `Afiliada ${v.afiliado}` : "Sem afiliado · só marcação de origem"} · final do link: <code>{v.sufixo || "—"}</code></p>
            </div>
            <Button size="sm" variant="outline" onClick={() => copiar(lista.filter((o) => (v.sufixo ? o.noCrm : true)).map((o) => `${o.produto} · ${o.tipo === "unica" ? "Única" : "Assinatura"} · ${o.condicao}\n${linkVendedora(o, v)}`).join("\n\n"))}><Copy className="h-4 w-4" /> {v.sufixo ? "Copiar lista do CRM" : "Copiar todos"}</Button>
          </header>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr><th className="p-3">Oferta</th><th className="p-3">Produto</th><th className="p-3">Tipo</th><th className="p-3">Cupom</th><th className="p-3">Valor</th><th className="p-3">Condição na Eduzz</th><th className="p-3">No FEO CRM</th><th className="p-3" /></tr>
              </thead>
              <tbody>
                {lista.map((o) => {
                  const url = linkVendedora(o, v);
                  return (
                    <tr key={o.id} className="border-t border-border">
                      <td className="p-3 whitespace-nowrap">{CAMPANHA_LABEL[o.campanha]}</td>
                      <td className="p-3 font-medium">{o.produto}</td>
                      <td className="p-3">{o.tipo === "unica" ? "Única" : "Assinatura"}</td>
                      <td className="p-3">{o.cupom ?? "—"}</td>
                      <td className="p-3 whitespace-nowrap">{brl(o.valor)}</td>
                      <td className="p-3"><span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-black-campaign-lilac" />{o.condicao}</span></td>
                      <td className="p-3">{o.noCrm ? "Sim" : "Só admin"}</td>
                      <td className="p-3 whitespace-nowrap">
                        <Button size="icon" variant="ghost" aria-label="Copiar link" onClick={() => copiar(url)}><Copy className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" asChild aria-label="Abrir checkout"><a href={url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" /></a></Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
        );
      })}
    </div></main>
  );
}
